import { useState } from "react";
import {
  useTeamAbsences,
  ABSENCE_PERIOD_LABELS,
  type AbsencePeriod,
  type AbsenceWithUser,
} from "@/hooks/use-absences";
import { useHighlight } from "@/hooks/use-highlight";
import { ABSENCE_TYPE_DOTS, ABSENCE_TYPE_LABELS, absenceDisplayHours } from "@/lib/absence-display";
import { formatAbsenceTimeRange } from "@/lib/absence-time";
import { formatDate, formatHours } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";

const PERIODS: AbsencePeriod[] = ["current-month", "previous-month", "year-to-date"];

/**
 * VIB-1653: lederens oppfølgingsliste for sykdom og ferie.
 *
 * Bevisst LESE-KUN: fravær har ingen godkjenningsflyt (VIB-1649) — det
 * registreres og reduserer kapasiteten umiddelbart. Denne listen er stedet
 * lederen lander når hen klikker et `absence_registered`-varsel, så hen kan
 * se hva som er meldt inn i sammenheng med resten av Timebank-flaten.
 *
 * `note` vises ikke: et sykefravær kan bære helseopplysninger, og bemanning
 * krever bare type og periode. Samme avgrensning som i varselteksten.
 */
export function TeamAbsenceFollowUp() {
  const [period, setPeriod] = useState<AbsencePeriod>("current-month");
  const { data: absences = [], isLoading } = useTeamAbsences(period);

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-display text-lg font-semibold tracking-[-0.01em]">
          Fravær til oppfølging
        </h2>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Periode">
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              aria-pressed={period === p}
              className={cn(
                "text-xs font-medium px-2.5 py-1 border-[1.5px] transition-colors",
                period === p
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:border-foreground hover:text-foreground",
              )}
            >
              {ABSENCE_PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Sykdom og ferie registreres fortløpende og reduserer kapasiteten med en
        gang. Ingenting her krever godkjenning.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <SrIcon
            name="loader2"
            className="w-5 h-5 animate-spin text-muted-foreground"
          />
        </div>
      ) : absences.length === 0 ? (
        <div className="border-[1.5px] border-foreground bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Ingen registrert sykdom eller ferie i perioden «
            {ABSENCE_PERIOD_LABELS[period].toLowerCase()}».
          </p>
        </div>
      ) : (
        <ul className="border-[1.5px] border-foreground bg-card divide-y divide-border">
          {absences.map((absence) => (
            <AbsenceRow key={absence.id} absence={absence} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AbsenceRow({ absence }: { absence: AbsenceWithUser }) {
  const ref = useHighlight<HTMLLIElement>(absence.id);
  const timeRange = formatAbsenceTimeRange(absence.start_time, absence.end_time);
  const isSingleDay = absence.start_date === absence.end_date;

  return (
    <li ref={ref} className="flex items-center gap-3 p-4">
      <span
        className={cn(
          "w-2 h-2 rounded-full shrink-0",
          ABSENCE_TYPE_DOTS[absence.type],
        )}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">
          {absence.users?.full_name ?? "Ukjent ansatt"}
        </p>
        <p className="text-xs text-muted-foreground">
          {ABSENCE_TYPE_LABELS[absence.type]} ·{" "}
          {isSingleDay
            ? formatDate(absence.start_date)
            : `${formatDate(absence.start_date)}–${formatDate(absence.end_date)}`}
          {timeRange ? ` · ${timeRange}` : ""}
          {" · "}
          {formatHours(absenceDisplayHours(absence))}
        </p>
      </div>
    </li>
  );
}
